import { Container, Grid, Paper, Card, Typography } from "@mui/material";
import AnalyticsDashboard from "../DataComponents/AnalyticsDashboard";
import AnalyticsTwoLevelPie from "../DataComponents/AnalyticsTwoLevelPie";
import AnalyticsBar from "../DataComponents/AnalyticsBar";
import Rankings from "../../Components/Rankings";
import { useEffect, useState } from "react";
import { useUser } from "../../UserContext";
import axios from "axios";
import { SelectChangeEvent } from "@mui/material";

// Define the interface for the product data
interface ProductData {
  product: string;
  total_sales: number;
}

const Analytics = () => {
  const { selectedBrandId } = useUser();
  const [dashboardData, setDashboardData] = useState<any[]>([]);
  const [rankings, setRankings] = useState<ProductData[]>([]);
  const [productData, setProductData] = useState<any[]>([]);
  const [innerPieData, setInnerPieData] = useState<
    { label: string; value: number }[]
  >([]);
  const [outerPieData, setOuterPieData] = useState<
    { label: string; value: number }[]
  >([]);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null); // State for selected product

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch sales overview data
        const overviewResponse = await axios.get(
          `${
            import.meta.env.VITE_API_URL
          }/analytics/data_sales_brand/${selectedBrandId}`
        );

        // Check if data is undefined or empty
        if (!overviewResponse.data || !overviewResponse.data[0]) {
          setDashboardData([]);
          setRankings([]);
          setProductData([]);
          return;
        }

        const overviewData = overviewResponse.data[0];
        console.log("overviewData", overviewData);
        // Prepare the dashboard data
        const formattedDashboardData = [
          { label: "# Events", value: Number(overviewData.total_events) || 0 },
          {
            label: "Total Hours",
            value: Math.round(Number(overviewData.total_hours) * 10) / 10 || 0,
          },
          {
            label: "Product Sold",
            value: Number(overviewData.total_sales) || 0,
          },
          {
            label: "Hourly Sales",
            value:
              Math.round(parseFloat(overviewData.sales_per_hour) * 10) / 10 ||
              0,
          },
        ];

        setDashboardData(formattedDashboardData);

        // Fetch product rankings data
        const rankingsResponse = await axios.get(
          `${
            import.meta.env.VITE_API_URL
          }/analytics/data_sales_product/${selectedBrandId}`
        );
        const productDataResponse = rankingsResponse.data;

        // Prepare rankings data
        const formattedRankings = productDataResponse
          .filter(
            (product: any) =>
              product.total_sales !== null && product.product !== null
          )
          .map((product: any, index: number) => ({
            rank: index + 1,
            product: product.product,
            sales: product.total_sales,
            sales_ethnicity: product.total_sales_ethnicity?.data || [],
          }))
          .sort(
            (a: { sales: number }, b: { sales: number }) => b.sales - a.sales
          )
          .slice(0, 10);

        setRankings(formattedRankings);
        setProductData(productDataResponse);

        // Set default selected product only if it's null
        if (selectedProduct === null && productDataResponse.length > 0) {
          setSelectedProduct(productDataResponse[0].product);
        }

        // Prepare inner and outer pie data based on selected product
        const selectedProductData = productDataResponse.find(
          (product: any) => product.product === selectedProduct
        );

        if (selectedProductData) {
          const { total_sales_ethnicity = {} } = selectedProductData;
          const innerData: { label: string; value: number }[] = [];
          const outerData: { label: string; value: number }[] = [];

          Object.entries(total_sales_ethnicity).forEach(
            ([ethnicity, data]: [string, any]) => {
              if (data.all !== undefined && Number(data.all) > 0) {
                innerData.push({
                  label: ethnicity
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase()),
                  value: Number(data.all),
                });

                outerData.push({
                  label: `${ethnicity
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase())} - Male`,
                  value: Number(data.male),
                });
                outerData.push({
                  label: `${ethnicity
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase())} - Female`,
                  value: Number(data.female),
                });
              }
            }
          );

          setInnerPieData(innerData);
          setOuterPieData(outerData);
          console.log(outerData);
          console.log(innerData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setDashboardData([]);
        setRankings([]);
        setProductData([]);
      }
    };

    if (selectedBrandId) {
      fetchData();
    }
  }, [selectedBrandId]); // Remove selectedProduct from dependencies

  useEffect(() => {
    // Prepare inner and outer pie data whenever selectedProduct changes
    const selectedProductData = productData.find(
      (product: any) => product.product === selectedProduct
    );

    if (selectedProductData) {
      const { total_sales_ethnicity = {} } = selectedProductData;
      const innerData: { label: string; value: number }[] = [];
      const outerData: { label: string; value: number }[] = [];

      Object.entries(total_sales_ethnicity).forEach(
        ([ethnicity, data]: [string, any]) => {
          if (data.all !== undefined && Number(data.all) > 0) {
            innerData.push({
              label: ethnicity
                .replace(/_/g, " ")
                .replace(/\b\w/g, (char) => char.toUpperCase()),
              value: Number(data.all),
            });

            outerData.push({
              label: `${ethnicity
                .replace(/_/g, " ")
                .replace(/\b\w/g, (char) => char.toUpperCase())} - Male`,
              value: Number(data.male),
            });
            outerData.push({
              label: `${ethnicity
                .replace(/_/g, " ")
                .replace(/\b\w/g, (char) => char.toUpperCase())} - Female`,
              value: Number(data.female),
            });
          }
        }
      );

      setInnerPieData(innerData);
      setOuterPieData(outerData);
    }
  }, [selectedProduct, productData]); // Add productData to dependencies

  const handleProductChange = (event: SelectChangeEvent<string>) => {
    setSelectedProduct(event.target.value); // Update selected product
  };

  return (
    <Container maxWidth="lg">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {dashboardData.length > 0 ? (
            <AnalyticsDashboard
              data={dashboardData}
              title="Top Line Performance"
            />
          ) : (
            <Card
              elevation={3}
              sx={{
                p: 4,
                backgroundColor: "background.paper",
                borderRadius: 2,
                textAlign: "center",
                minHeight: "200px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h5" color="text.secondary">
                No Data Available
              </Typography>
            </Card>
          )}
        </Grid>

        {dashboardData.length > 0 ? (
          <>
            <Grid item xs={6}>
              {productData.length > 0 && (
                <AnalyticsBar
                  data={productData}
                  title="Sales Per Hour"
                  xKey="product"
                  series={[
                    {
                      name: "Sales Per Hour",
                      data: productData.map((item) => item.sales_per_hour),
                    },
                  ]}
                />
              )}
            </Grid>

            <Grid item xs={6}>
              <AnalyticsTwoLevelPie
                data1={innerPieData}
                data2={outerPieData}
                title="Product Sales by Ethnicity"
                filters={productData.map((product) => product.product)}
                selectedProduct={selectedProduct}
                onProductChange={handleProductChange}
              />
            </Grid>

            <Grid item xs={12}>
              {rankings.length > 0 && (
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    backgroundColor: "background.paper",
                    borderRadius: 2,
                  }}
                >
                  <Rankings
                    columns={[
                      { id: "rank", label: "Rank" },
                      { id: "product", label: "Product" },
                      { id: "sales", label: "Sales" },
                    ]}
                    data={rankings}
                  />
                </Paper>
              )}
            </Grid>
          </>
        ) : null}
      </Grid>
    </Container>
  );
};

export default Analytics;
